'use client';
import React, { useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ManageAccount } from './index';
import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { useUser } from '@/utils/supabase/useUser';
import { hasEmptyPersonalInfo } from './hasEmptyPersonalInfo';
import { ThemeProvider } from '@mui/system';
import theme from '@/app/(signup)/theme';
import { usePopups, PopupTypes } from '@/providers/popups';
import { addDays, isAfter } from 'date-fns';

export const ManageAccountModal = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { contact, userDetails } = useUser();
  const { openPopup } = usePopups();

  const evaluateShouldOpen = useCallback(() => {
    if (!userDetails || !contact) return;

    // Check if updated_at is more than 60 days ago
    let dueForUpdate = false;
    if (contact.updated_at) {
      const now = new Date();
      const updatedAt = new Date(contact.updated_at);
      const blockUntil = addDays(updatedAt, 60);
      dueForUpdate = isAfter(now, blockUntil);
    } else {
      dueForUpdate = true;
    }

    const shouldOpen = hasEmptyPersonalInfo(userDetails) || dueForUpdate;

    if (shouldOpen) {
      openPopup(PopupTypes.PersonalDetails);
    }
  }, [contact, userDetails, openPopup]);

  useEffect(() => {
    setTimeout(() => {
      evaluateShouldOpen();
    }, 5000); // Show after 5 seconds
  }, [evaluateShouldOpen]);

  const handleClose = () => {
    onClose();
  };

  return (
    userDetails && (
      <ThemeProvider theme={theme}>
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
          <DialogTitle>
            Are your details correct?
            <IconButton
              aria-label='close'
              onClick={handleClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <ManageAccount userDetails={userDetails} onSuccess={handleClose}>
              <PersonalInfoSection
                showPersonalInfo={[
                  'firstName',
                  'lastName',
                  'mobile',
                  'company',
                  'position',
                  'city',
                  'industry'
                ]}
              />
            </ManageAccount>
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    )
  );
};
